import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import { useEffect, useState } from "react";
import { readWithFotos } from "../services/AlbumService";
import { Album } from "../models/Album";

const AlbumView = () => {
  const { id } = useParams<{ id?: string }>();

  const [album, setAlbum] = useState<Album | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      return;
    }

    const fetchAlbum = async () => {
      try {
        const result = await readWithFotos(id);

        if (!result) {
          setNotFound(true);
          return;
        }

        setAlbum(result);
      } catch (error) {
        console.log("Erro ao carregar o álbum:", error);
        setNotFound(true);
      }
    };

    fetchAlbum();
  }, [id]);

  const isVideo = (fileName?: string) => {
    if (!fileName) return false;
    return /\.(mp4|webm|ogg|mov)$/i.test(fileName);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-romantic text-5xl text-foreground mb-4">
              Álbum não encontrado
            </h1>
            <Link
              to="/albums"
              className="text-primary hover:underline font-serif"
            >
              Voltar para álbuns
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!album) {
    // loading simples (opcional)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 text-center text-muted-foreground font-serif">
          Carregando álbum...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/albums"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-8 font-serif"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar para álbuns
          </Link>

          <div className="mb-12 animate-fade-in">
            <h1 className="font-romantic text-5xl md:text-7xl text-foreground mb-4">
              {album.nome}
            </h1>

            <div className="flex items-center gap-4 text-muted-foreground mb-4">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                <span className="font-serif">
                  {album.fotos?.length ?? 0} fotos
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {album.fotos?.map((photo, index) => (
              <div
                key={photo.id}
                className="group animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="rounded-2xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 border-4 border-white">
                  <div className="relative">
                    <div className="relative">
                      {photo.video ? (
                        <video controls playsInline className="w-full max-h-[90vh]">
                          <source src={photo.url} />
                        </video>
                      ) : (
                        <a
                          href={photo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block relative"
                        >
                        <img src={photo.url} className="w-full max-h-[90vh]" />
                        </a>
                      )}
                </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-block bg-card/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border-2 border-primary/20">
              <p className="font-romantic text-3xl text-primary">
                Momentos que vivemos juntos ♡
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AlbumView;
